export type InviteRequestData = {
  email: string
  culturalIdentities: string[]
  phone?: string
  socialProfile?: string
  reason?: string
}

export type InviteRequestResponse = {
  success: boolean
  message?: string
  id?: string
}

export const EMAIL_ALREADY_REGISTERED = 'EMAIL_ALREADY_REGISTERED'

/**
 * Error thrown when the invite API rejects a request.
 */
export class InviteRequestApiError extends Error {
  readonly status: number
  readonly code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'InviteRequestApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Submits an invite request to the backend API
 * The backend API endpoint should use @neondatabase/serverless to store the data
 */
export async function submitInviteRequest(
  data: InviteRequestData,
): Promise<InviteRequestResponse> {
  // TODO: Replace with your actual API endpoint URL
  // This should point to a backend endpoint that uses @neondatabase/serverless
  const API_ENDPOINT = `${process.env.EXPO_PUBLIC_API_URL || 'https://api.grapevyne.space'}/api/invite-request`

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        error?: string
        message?: string
        code?: string
      }
      throw new InviteRequestApiError(
        errorData.error ||
          errorData.message ||
          'Failed to submit invite request',
        response.status,
        errorData.code,
      )
    }

    const result = (await response.json()) as {
      message?: string
      id?: string
      data?: {id?: string}
    }
    return {
      success: true,
      message: result.message,
      id: result.id ?? result.data?.id,
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    throw new Error('An unexpected error occurred')
  }
}
